import React from 'react';
import { Helmet } from 'react-helmet';
import './BlogReactResponsive.css'; // Make sure to create Blog.css for styling code blocks, etc.

function BlogReactResponsive() {
  return (
    <div className="blog-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>How to Build a Responsive Website with React.js | SamSoftix</title>
        <meta
          name="description"
          content="Step-by-step guide to building a responsive website with React.js. Learn components, layout, CSS, and mobile-first design from SamSoftix experts."
        />
        <meta
          name="keywords"
          content="React.js tutorial, responsive website, web development, coding training, SamSoftix, front-end development, mobile-friendly website"
        />
        <link rel="canonical" href="https://www.samsoftix.com/Create_React_Js_App" />
      </Helmet>

      {/* Blog Header */}
      <header className="blog-header">
        <h1>How to Build a Responsive Website with React.js</h1>
        <p>Learn step-by-step how to create a responsive, mobile-friendly website using React.js.</p>
      </header>

      {/* Introduction */}
      <section>
        <h2>Introduction</h2>
        <p>
          Building a responsive website is essential in today’s mobile-first world. React.js allows developers
          to create dynamic, fast, and scalable web applications. In this tutorial, you’ll learn
          how to build a fully responsive website using React.js, including layout, components, and styling.
        </p>
      </section>

      {/* Why React.js */}
      <section>
        <h2>Why Choose React.js for Responsive Websites?</h2>
        <ul>
          <li><strong>Component-Based Architecture:</strong> Build reusable UI components for faster development.</li>
          <li><strong>Virtual DOM:</strong> Smooth performance with fast rendering.</li>
          <li><strong>Flexible Styling:</strong> Works with CSS, Sass, or CSS-in-JS libraries.</li>
          <li><strong>Responsive Design Friendly:</strong> Supports Flexbox, Grid, and media queries.</li>
        </ul>
      </section>

      {/* Step 1 */}
      <section>
        <h2>Step 1: Set Up Your React Project</h2>
        <p>Install Node.js, then create a React project using Create React App:</p>
        <pre>
          <code>
{`npx create-react-app responsive-website
cd responsive-website
npm start`}
          </code>
        </pre>
        <p>Your default React app will launch at <strong>http://localhost:3000</strong>.</p>
      </section>

      {/* Step 2 */}
      <section>
        <h2>Step 2: Plan Your Website Layout</h2>
        <p>
          Common sections: <strong>Header, Hero, Services, About, Portfolio, Contact, Footer</strong>.
          Break these into reusable React components:
        </p>
        <pre>
          <code>
{`components/
  Header.js
  Hero.js
  Services.js
  About.js
  Portfolio.js
  Contact.js
  Footer.js`}
          </code>
        </pre>
      </section>

      {/* Step 3 */}
      <section>
        <h2>Step 3: Create Components</h2>
        <p>Example: Header Component</p>
        <pre>
          <code>
{`import React from 'react';
import './Header.css';

function Header() {
  return (
    <header className="header">
      <h1>My Responsive Website</h1>
      <nav>
        <ul>
          <li>Home</li>
          <li>About</li>
          <li>Services</li>
          <li>Contact</li>
        </ul>
      </nav>
    </header>
  );
}

export default Header;`}
          </code>
        </pre>
      </section>

      {/* Step 4 */}
      <section>
        <h2>Step 4: Make It Responsive with CSS</h2>
        <p>Use Flexbox or CSS Grid along with media queries:</p>
        <pre>
          <code>
{`.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5rem 2rem;
  text-align: center;
}

.hero h2 {
  font-size: 2rem;
}

@media (max-width: 768px) {
  .hero h2 {
    font-size: 1.5rem;
  }
}`}
          </code>
        </pre>
      </section>

      {/* Step 5 */}
      <section>
        <h2>Step 5: Reusable Components</h2>
        <pre>
          <code>
{`function ServiceCard({ title, description }) {
  return (
    <div className="service-card">
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  );
}`}
          </code>
        </pre>
      </section>

      {/* Step 6 */}
      <section>
        <h2>Step 6: Navigation & Mobile Menu</h2>
        <p>Add a hamburger menu and toggle state in React for smaller screens.</p>
      </section>

      {/* Step 7 */}
      <section>
        <h2>Step 7: Footer Component</h2>
        <p>Include contact info, social links, and copyright notice. Make it responsive by stacking elements on mobile.</p>
      </section>

      {/* Step 8 */}
      <section>
        <h2>Step 8: Test Responsiveness</h2>
        <p>Use Chrome DevTools Device Toolbar to test different screen sizes.</p>
      </section>

      {/* Step 9 */}
      <section>
        <h2>Step 9: Deploy Your Website</h2>
        <p>Deploy using <strong>Vercel, Netlify, or GitHub Pages</strong>:</p>
        <pre>
          <code>
{`npm run build
# Follow hosting instructions for your chosen platform`}
          </code>
        </pre>
      </section>

      {/* Conclusion */}
      <section>
        <h2>Conclusion</h2>
        <p>
          Building a responsive website with React.js ensures better user experience across devices. By
          using reusable components, Flexbox/Grid, and media queries, you can create professional
          websites for clients or personal projects.
        </p>
        <p>
          <strong>SamSoftix</strong> offers expert React.js web development and remote coding training programs.
          Contact us to create modern, scalable websites or learn from industry professionals.
        </p>
      </section>
    </div>
  );
}

export default BlogReactResponsive;
